<template>
  <LayoutPage>
    <!-- Header row: page title, and the way back up to the org-wide report -->
    <template #title>
      <v-toolbar-title class="headline">
        <span class="print-title-prefix">{{ printTitlePrefix }}</span>
        Inventory
      </v-toolbar-title>
    </template>
    <template #actions>
      <!--
        A single-facility manager has no inventory summary to go back to, so
        there is no second header row and everything stays on the toolbar.
      -->
      <InventoryHeaderActions
        v-if="singleFacilityScope"
        :tabs="tabDefinitions"
        :current-tab="currentTabIndex"
        :search.sync="search"
        :search-loading="isSearchLoading"
        @tab-change="tab = $event"
        @scan="showQRDialog = true"
      />

      <v-btn v-else class="action-btn" color="primary" @click="goBack">
        <v-icon left>mdi-arrow-left</v-icon>
        Go to Inventory Summary
      </v-btn>
    </template>

    <!-- Action row: which inventory to look at, then search and the CTAs -->
    <InventoryHeaderActions
      v-if="!singleFacilityScope"
      class="mb-4"
      stacked
      :tabs="tabDefinitions"
      :current-tab="currentTabIndex"
      :search.sync="search"
      :search-loading="isSearchLoading"
      @tab-change="tab = $event"
      @scan="showQRDialog = true"
    />

    <InventoryAssignableTable
      v-if="currentTabIndex === 0"
      ref="assignableTable"
      :key="`tab-0-${tabReloadKey}`"
      :search="search"
    />
    <InventoryPreinstalledTable
      v-else-if="currentTabIndex === 1"
      ref="preinstalledTable"
      :key="`tab-1-${tabReloadKey}`"
      :search="search"
    />
    <InventoryUnavailableTable
      v-else
      ref="unavailableTable"
      :key="`tab-2-${tabReloadKey}`"
      :search="search"
    />

    <QRCodeDialog
      v-model="showQRDialog"
      title="Begin Pre-Install"
      description="Scan this QR code with your mobile phone or tablet to begin assigning the devices."
      :url-generator="generateInventoryUrl"
    />
  </LayoutPage>
</template>

<script>
import InventoryAssignableTable from "./inventory/InventoryAssignableTable.vue";
import InventoryPreinstalledTable from "./inventory/InventoryPreinstalledTable.vue";
import InventoryUnavailableTable from "./inventory/InventoryUnavailableTable.vue";
import InventoryHeaderActions from "./inventory/InventoryHeaderActions.vue";
import LayoutPage from "@layout/LayoutPage.vue";
import { debounce } from "lodash";
import useInventory from "./useInventory";
import useRouteQuery from "@components/common/useRouteQuery";
import useUser from "@components/authentication/useUser";
import { onUnmounted, ref } from "vue";
import { formatTime12hr, durationInWords } from "@tod-ui/helpers/datetime";
import QRCodeDialog from "../mobile/QRCodeDialog.vue";

export default {
  name: "PageInventory",
  components: {
    InventoryAssignableTable,
    InventoryPreinstalledTable,
    InventoryUnavailableTable,
    InventoryHeaderActions,
    LayoutPage,
    QRCodeDialog,
  },

  setup() {
    const {
      fetchTabData,
      fetchEndedServiceUnits,
      cleanUpInventoryRefs,
      officeItemsPerPage,
      preinstalledItemsPerPage,
      unavailableItemsPerPage,
      resetPagination,
    } = useInventory();

    const { singleFacilityScope } = useUser();

    const tabReloadKey = ref(0);
    const lastSearchedText = ref("");
    const { tab } = useRouteQuery();

    const tabDefinitions = [
      {
        name: "Office Inventory",
        component: InventoryAssignableTable,
        getItemsPerPage: () => officeItemsPerPage.value,
      },
      {
        name: "Pre-installed Inventory",
        component: InventoryPreinstalledTable,
        getItemsPerPage: () => preinstalledItemsPerPage.value,
      },
      {
        name: "Unavailable Inventory",
        component: InventoryUnavailableTable,
        getItemsPerPage: () => unavailableItemsPerPage.value,
      },
    ];

    const getItemsPerPageForTab = (tabIndex) => {
      const safeIndex = parseInt(tabIndex || 0);
      return tabDefinitions[safeIndex]?.getItemsPerPage() || 10;
    };

    onUnmounted(cleanUpInventoryRefs);

    return {
      fetchTabData,
      fetchEndedServiceUnits,
      tab,
      tabReloadKey,
      lastSearchedText,
      resetPagination,
      tabDefinitions,
      getItemsPerPageForTab,
      singleFacilityScope,
    };
  },

  data() {
    return {
      search: "",
      previousTab: null,
      searchTimeout: null,
      isSearchLoading: false,
      showQRDialog: false,
      mobileInventoryUrl: null,
      qrToken: null,
      qrError: null,
      qrExpirationTime: null,
    };
  },

  computed: {
    currentTabIndex() {
      return parseInt(this.tab || 0);
    },

    printTitlePrefix() {
      const titles = ["Office", "Pre-installed", "Unavailable"];
      return titles[this.currentTabIndex] || "Office";
    },

    currentTabComponent() {
      const components = [
        "assignableTable",
        "preinstalledTable",
        "unavailableTable",
      ];
      return components[this.currentTabIndex] || "assignableTable";
    },

    formattedExpiryTime() {
      if (!this.qrExpirationTime) return null;
      return formatTime12hr(this.qrExpirationTime);
    },

    timeRemainingDuration() {
      if (!this.qrExpirationTime) return null;
      return durationInWords(new Date().toISOString(), this.qrExpirationTime);
    },

    computedQrError() {
      if (!this.qrError) return null;

      return {
        ...this.qrError,
        expiryTime: this.formattedExpiryTime,
        timeRemaining: this.timeRemainingDuration,
      };
    },
  },

  watch: {
    search(newValue, oldValue) {
      this.debouncedSearch();
    },

    tab(newValue, oldValue) {
      this.handleTabChange(newValue, oldValue);
    },
  },

  beforeMount() {
    this.loadInitialData();
  },
  created() {
    // Create debounced search function that uses latest search value
    this.debouncedSearch = debounce(() => {
      // Get current search value at time of execution
      const currentSearchValue = this.search;

      // Execute search with current value
      this.executeSearch(currentSearchValue);
    }, 1000);
  },

  methods: {
    goBack() {
      return this.$router
        .push({ name: "OrganizationInventory" })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") throw err;
        });
    },

    switchToTab(tabIndex) {
      // Route will be updated, which will trigger the tab watcher
      this.tab = tabIndex;
    },

    async loadInitialData() {
      // Initial load with normal pagination
      await this.fetchTabData(
        this.currentTabIndex,
        this.$apollo.provider.defaultClient,
        10
      );
      this.fetchEndedServiceUnits(this.$apollo.provider.defaultClient);
    },

    handleTabChange(newValue, oldValue) {
      // Store previous tab
      this.previousTab = oldValue;
      const currentTab = parseInt(newValue || 0);
      const itemsPerPage = this.getItemsPerPageForTab(currentTab);

      this.tabReloadKey++;

      this.$nextTick(() => {
        if (this.$refs[this.currentTabComponent]) {
          this.$refs[this.currentTabComponent].tableState.itemsPerPage =
            itemsPerPage;
        }

        this.fetchTabData(currentTab, this.$apollo, itemsPerPage, this.search);
      });
    },

    executeSearch(searchValue) {
      const currentTab = this.currentTabIndex;

      // When search is cleared (and was previously not empty)
      if (searchValue === "" && this.lastSearchedText !== "") {
        this.handleSearchClear(currentTab);
        this.isSearchLoading = false;
        return;
      }

      this.lastSearchedText = searchValue;

      if (searchValue) {
        this.handleSearch(currentTab, searchValue);
      }
    },

    handleSearch(tabIndex, searchText) {
      this.fetchTabData(tabIndex, this.$apollo, -1, searchText);
      this.tabReloadKey++;
    },

    handleSearchClear(tabIndex) {
      this.resetTab(tabIndex);
      this.lastSearchedText = "";
      this.tabReloadKey++;

      this.$nextTick(() => {
        if (this.$refs[this.currentTabComponent]) {
          this.$refs[this.currentTabComponent].tableState.itemsPerPage = 10;
        }
        this.isSearchLoading = false;
      });
    },

    resetTab(tabIndex) {
      this.resetPagination(tabIndex);
      this.fetchTabData(tabIndex, this.$apollo, 10, "");
    },

    debouncedSearch: debounce(function () {
      if (this.search) {
        // For search, use the special search method that sets limit to totalCount
        this.fetchTabDataWithSearch(
          this.currentTabIndex,
          this.$apollo.provider.defaultClient,
          this.search
        );
      } else {
        // When search is cleared, go back to normal pagination
        this.fetchTabData(
          this.currentTabIndex,
          this.$apollo.provider.defaultClient,
          10
        );
      }
    }, 100),

    generateInventoryUrl(qrToken) {
      const baseUrl = window.location.origin;
      const facilityId = this.$route.params.organization_id;

      return `${baseUrl}/mobile-inventory?qrToken=${encodeURIComponent(
        qrToken
      )}&facility=${facilityId}`;
    },

    resetQrState() {
      this.qrError = null;
      this.qrExpirationTime = null;
      this.mobileInventoryUrl = null;
      this.qrToken = null;
    },
  },
};
</script>

<style scoped>
.print-title-prefix {
  display: none;
}

@media print {
  .print-title-prefix {
    display: inline;
  }
}

.qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.qr-canvas {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-container {
  padding: 16px 0;
}

.error-content p {
  margin-bottom: 8px;
}

.error-content .caption {
  color: rgba(0, 0, 0, 0.6);
}
</style>
